import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFlagBridge, FlagEditorLayer, openMenuAtCursor } from '../flags/useFlagEditor';
import { useEditor, EditorContent } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import { SelectionBar } from './SelectionBar';
import type { JSONContent } from '@tiptap/react';
import { EditorToolbar } from './EditorToolbar';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useToast } from '../../contexts/ToastContext';
import { createMentionSuggestion } from './MentionExtension';
import { createFollowUpExtension } from './FollowUpExtension';
import { dossierExtensions } from './editorExtensions';
import { useExcerptEditor } from './useExcerptEditor';
import { useRevealEntity } from './revealEntity';
import type { DocRef } from '../../contexts/ExcerptSyncContext';
import './editor-styles.css';
import { ListPicker } from './lists/ListPicker';
import { listsIn, selectionLinePositions, type OpenListPicker } from './lists/NamedListNodes';
import { turnItemIntoTask, turnOpenItemsIntoTasks, fetchCarry, insertCarried, localToday } from '../../lib/lists';

interface DossierEditorProps {
  content: JSONContent | null;
  onUpdate: (content: JSONContent) => void;
  editable?: boolean;
  placeholder?: string;
  meetingInstanceId?: string;
  /** Owning scratchpad. Enables file attachment; absent elsewhere. */
  scratchpadId?: string;
  onTaskCreated?: () => void;
  onNoteCreated?: () => void;
  onTaskStatusChange?: (taskId: string, newStatus: string) => void;
  editorRef?: (editor: Editor | null) => void;
  onMentionInserted?: (personId: string, personName: string) => void;
  onFollowUpUndo?: (taskId: string) => void;
  onFollowUpTextChanged?: (taskId: string, newText: string) => void;
  /**
   * The document this editor holds, when it is one excerpts can come from and
   * be sent to (#277). Absent for templates, View annotations and previews.
   */
  excerptHost?: DocRef;
}

export function DossierEditor({
  content,
  onUpdate,
  editable = true,
  placeholder = 'Start writing...',
  meetingInstanceId,
  scratchpadId,
  onTaskCreated,
  onNoteCreated,
  onTaskStatusChange,
  editorRef,
  onMentionInserted,
  onFollowUpUndo,
  onFollowUpTextChanged,
  excerptHost,
}: DossierEditorProps) {
  const { prefs } = usePreferences();
  const [toolbarVisible, setToolbarVisible] = useState(prefs['editor.toolbarVisible'] !== 'false');
  const toast = useToast();
  const handleImageError = useCallback((message: string) => toast.error(message), [toast]);

  // Use refs to keep callbacks stable for extensions
  const onMentionInsertedRef = useRef(onMentionInserted);
  useEffect(() => { onMentionInsertedRef.current = onMentionInserted; }, [onMentionInserted]);

  const onFollowUpUndoRef = useRef(onFollowUpUndo);
  useEffect(() => { onFollowUpUndoRef.current = onFollowUpUndo; }, [onFollowUpUndo]);

  const onFollowUpTextChangedRef = useRef(onFollowUpTextChanged);
  useEffect(() => { onFollowUpTextChangedRef.current = onFollowUpTextChanged; }, [onFollowUpTextChanged]);

  // Create stable mention suggestion that reads from ref
  const mentionSuggestion = useMemo(() => createMentionSuggestion({
    onMentionInserted: (personId, personName) => {
      onMentionInsertedRef.current?.(personId, personName);
    },
  }), []);

  // Create follow-up extension (stable)
  const followUpExtension = useMemo(() => createFollowUpExtension({
    onUndo: (taskId) => {
      onFollowUpUndoRef.current?.(taskId);
    },
    onTextChanged: (taskId, newText) => {
      onFollowUpTextChangedRef.current?.(taskId, newText);
    },
  }), []);

  // Flags (#316): only a document that can be a source carries them.
  const flagBridge = useFlagBridge(excerptHost);

  // Named lists (#337): the "which list?" picker, opened by Alt+L, the ">>"
  // prefix, the right-click menu and the bar/toolbar buttons.
  const [picker, setPicker] = useState<Parameters<OpenListPicker>[0] | null>(null);
  // Turn into task needs a document to link the task to; Bring forward needs a meeting.
  const listEditorRef = useRef<Editor | null>(null);
  const lists = useMemo(() => {
    const source = excerptHost;
    const fail = () => toast.error("Couldn't make the task. Try again.");
    return {
      onOpenListPicker: (a: Parameters<OpenListPicker>[0]) => setPicker(a),
      onTurnItemIntoTask: source ? (itemId: string) => {
        const ed = listEditorRef.current;
        if (ed) turnItemIntoTask(ed, itemId, source).then((id) => { if (id) toast.success('Task created'); }).catch(fail);
      } : undefined,
      onTurnOpenItemsIntoTasks: source ? (listId: string) => {
        const ed = listEditorRef.current;
        if (ed) turnOpenItemsIntoTasks(ed, listId, source)
          .then((n) => toast.success(n ? `${n} task${n === 1 ? '' : 's'} created` : 'No open items without a task'))
          .catch(fail);
      } : undefined,
      onBringForward: meetingInstanceId ? () => {
        const ed = listEditorRef.current;
        if (!ed) return;
        fetchCarry(meetingInstanceId, localToday())
          .then(({ lists: carry }) => {
            const n = insertCarried(ed, carry);
            toast.success(n ? `Brought forward ${n} open item${n === 1 ? '' : 's'}` : 'Nothing open to bring forward');
          })
          .catch(() => toast.error("Couldn't bring items forward. Try again."));
      } : undefined,
    };
    // Extensions are built once per editor; the editor is remounted (key) when the document changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editor = useEditor({
    extensions: dossierExtensions({
      placeholder,
      handleImageError,
      mentionSuggestion,
      onTaskStatusChange,
      scratchpadId,
      followUpExtension,
      flags: flagBridge.options,
      lists,
    }),
    content: content ?? undefined,
    editable,
    onUpdate: ({ editor: ed }) => {
      onUpdate(ed.getJSON());
    },
    editorProps: {
      attributes: {
        class: 'tiptap',
      },
    },
  });

  const excerpts = useExcerptEditor(editor, excerptHost);
  useEffect(() => { listEditorRef.current = editor; }, [editor]);

  const openListPicker = useCallback(() => {
    if (!editor) return;
    let x = 0;
    let y = 0;
    try { const c = editor.view.coordsAtPos(editor.state.selection.from); x = c.left; y = c.bottom; } catch { /* no layout */ }
    setPicker({ positions: selectionLinePositions(editor.state), x, y });
  }, [editor]);
  useRevealEntity(editor);

  // Expose editor instance to parent
  useEffect(() => {
    editorRef?.(editor);
    return () => editorRef?.(null);
  }, [editor, editorRef]);

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-bg-card">
      {editable && toolbarVisible && (
        <EditorToolbar
          editor={editor}
          meetingInstanceId={meetingInstanceId}
          scratchpadId={scratchpadId}
          onTaskCreated={onTaskCreated}
          onNoteCreated={onNoteCreated}
          onCreateExcerptItem={excerpts.bar?.onCreateItem}
          onExcerpt={excerpts.bar?.onExcerpt}
          onAddToList={openListPicker}
          onOpenFlagMenu={excerptHost && editor ? (anchor) => openMenuAtCursor(editor, flagBridge, anchor) : undefined}
        />
      )}
      {editable && !toolbarVisible && (
        <button
          onClick={() => setToolbarVisible(true)}
          className="w-full px-2 py-0.5 text-[10px] text-text-muted hover:text-text-secondary hover:bg-bg-hover transition-colors text-left"
          title="Show formatting toolbar"
        >
          Aa
        </button>
      )}
      {excerpts.header}
      <EditorContent editor={editor} />
      {editable && editor && excerptHost && <FlagEditorLayer editor={editor} host={excerptHost} bridge={flagBridge} onAddToList={setPicker} />}
      {editable && editor && (
        <SelectionBar
          editor={editor}
          meetingInstanceId={meetingInstanceId}
          scratchpadId={scratchpadId}
          onTaskCreated={onTaskCreated}
          onNoteCreated={onNoteCreated}
          excerpts={excerpts.bar}
          onFlag={excerptHost ? (anchor) => openMenuAtCursor(editor, flagBridge, anchor) : undefined}
          onAddToList={openListPicker}
        />
      )}
      {excerpts.overlays}
      {picker && editor && (
        <ListPicker
          x={picker.x}
          y={picker.y}
          filter={picker.filter}
          lists={listsIn(editor.state.doc)}
          onPick={(listId) => { editor.chain().focus().fileLinesIntoList(listId, picker.positions).run(); setPicker(null); }}
          onCreate={(name) => {
            const listId = crypto.randomUUID();
            editor.chain().focus()
              .insertContentAt(editor.state.doc.content.size, { type: 'namedList', attrs: { listId, name, kind: 'checklist' } })
              .fileLinesIntoList(listId, picker.positions)
              .run();
            setPicker(null);
          }}
          onClose={() => { setPicker(null); editor.commands.focus(); }}
        />
      )}
    </div>
  );
}
